<!--
 * 文件功能：登录用户首页主体（个人信息侧栏 + 博客瀑布流，动森风格）
 * 输入参数：无（从 localStorage "honey-user" 读取当前登录用户）
 * 输出参数：无（渲染用户信息卡片、个人时间线与博客瀑布流）
 * 返回值：无
 * 说明：Vue 3 `<script setup lang="ts">` 重写版（原 vue-property-decorator class 组件）。
 *       - 业务逻辑完整保留：
 *         1. POST /guest/get_all_logs 获取全部博客日志（瀑布流数据源）；
 *         2. GET /user/select_user_msg/{id} 获取用户最新资料（badge 标签 / timeline 时间线覆盖默认值）；
 *         3. localStorage "honey-user" 登录态校验，未登录跳转 /login；
 *         4. 点击博客卡片以 name: 'blog_show' + params（整条博客对象）跳转，路由目标与原版一致。
 *       - 视觉动森化：Title 飘带标题、Typewriter 打字机欢迎语、羊皮纸 Card（app-yellow + 波点）、
 *         Tag 个人标签、Divider 装饰分隔、Image 相框图片（点击可预览、按原始宽高比自适应高度
 *         实现真瀑布流）、Icon 装饰；页面内 Time 时钟已移除（与全局导航栏时钟重复）。
 -->
<template>
  <div class="ac-home">
    <!-- 顶部欢迎区：飘带标题 + 打字机欢迎语（时钟由全局导航栏 Time 提供，去除重复） -->
    <header class="ac-home__head">
      <div class="ac-home__head-text">
        <Title size="large" color="app-orange">🏝 我的岛屿小屋</Title>
        <Typewriter :speed="60">
          <p class="ac-home__welcome">
            欢迎回到动森小岛！这里记录着克利夫兰程序员的生活、思考与爱情故事 🌴
          </p>
        </Typewriter>
      </div>
    </header>
    <Divider type="wave-yellow" />

    <el-container class="ac-home__body">
      <!-- 左侧侧栏：个人信息羊皮纸卡片 -->
      <el-aside width="330px" class="ac-home__aside">
        <Card color="app-yellow" pattern="default" class="user-card">
          <div class="user-card__head">
            <el-avatar :src="userLogoSrc" :size="96" class="user-card__avatar" />
            <div class="user-card__name">克利夫兰程序员</div>
          </div>
          <Divider type="dashed-brown" />
          <div class="user-card__tags">
            <Tag
              v-for="(item, index) in userTagArr"
              :key="index"
              :color="tagColorMap[item.type] || 'default'"
              size="small"
            >
              {{ item.text }}
            </Tag>
          </div>
          <Divider type="dashed-brown" />
          <el-timeline class="user-card__timeline">
            <el-timeline-item
              v-for="(item, index) in timelineArr"
              :key="index"
              :timestamp="item.timestamp"
              :type="item.type"
            >
              {{ item.content }}
            </el-timeline-item>
          </el-timeline>
        </Card>
      </el-aside>

      <!-- 右侧主区：博客瀑布流 -->
      <el-main class="ac-home__main">
        <Card class="blog-card">
          <div class="blog-card__head">
            <Icon name="icon-camera" :size="26" bounce />
            <Title color="app-blue">最新日志</Title>
          </div>
          <!-- 瀑布流加载骨架（动森 Skeleton 占位，加载完成后切换为真实卡片） -->
          <div v-if="blogsLoading" class="masonry-container">
            <div v-for="n in 6" :key="n" class="masonry-skeleton">
              <Skeleton :loading="true" variant="rect" :height="n % 3 === 0 ? 200 : 150" />
              <Skeleton :loading="true" variant="text" width="70%" class="masonry-skeleton__text" />
            </div>
          </div>

          <div v-else class="masonry-container">
            <Card
              v-for="(item, index) in blogArr"
              :key="index"
              class="masonry-item"
              @click="showThisBlog(item)"
            >
              <div v-if="item.image" class="masonry-item__pic" @click.stop>
                <!-- 不传 height：图片按原始宽高比自适应高度，形成真正的瀑布流 -->
                <Image :src="normalizeImageUrl(item.image)" :alt="item.title" width="100%" lazy />
              </div>
              <div class="masonry-item__title">
                <span>{{ item.title }}</span>
              </div>
            </Card>
          </div>
          <Card v-if="!blogsLoading && blogArr.length === 0" type="dashed" class="masonry-empty">
            <p>🏝️ 岛屿日志正在搬运中，敬请期待…</p>
          </Card>
        </Card>
      </el-main>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Title, Card, Tag, Divider, Icon, Image, Skeleton, Typewriter } from 'animal-island-vue'
import request from '@/utils/request'
import { normalizeImageUrl } from '@/utils/imageUrl'
import userLogoSrc from '@/assets/user-logo/admin_logo.jpg'

const router = useRouter()

/** 博客日志条目（后端 /guest/get_all_logs 返回结构） */
interface BlogItem {
  title?: string
  image?: string | null
  [key: string]: any
}

/** el-tag 类型 → animal-island-vue Tag 颜色的映射表 */
const tagColorMap: Record<string, string> = {
  danger: 'app-red',
  primary: 'app-blue',
  success: 'app-green',
  warning: 'app-yellow',
  info: 'brown',
}

/** 个人标签（登录后由 /user/select_user_msg 的 badge 字段覆盖） */
const userTagArr = ref<Array<{ type: string; text: string }>>([
  { type: 'danger', text: '共产主义者' },
  { type: 'primary', text: '二次元' },
  { type: 'success', text: '爱打篮球' },
  { type: 'primary', text: '电子游戏中度爱好者' },
  { type: 'danger', text: '做梦想当个作家' },
])

/** 个人时间线（登录后由 /user/select_user_msg 的 timeline 字段覆盖） */
const timelineArr = ref<Array<{ timestamp: string; content: string; type: string }>>([
  { timestamp: '2016-9', content: '邯郸学院嵌入式技术与应用专业入学', type: 'success' },
  { timestamp: '2019-05', content: '北京星震同源数字系统股份有限公司实习', type: 'success' },
  { timestamp: '2019-07', content: '北京星震同源数字系统股份有限公司实习结束', type: 'success' },
  { timestamp: '2019-7', content: '邯郸学院嵌入式技术与应用专业毕业', type: 'success' },
  { timestamp: '2019-9', content: '入职基成科技（天津）有限公司', type: 'success' },
  { timestamp: '2021-03', content: '入学河北工程大学计算机科学与技术专业', type: 'success' },
  { timestamp: '2023-07', content: '河北工程大学计算机科学与技术专业毕业', type: 'success' },
  { timestamp: '2023-10', content: '基成科技（天津）有限公司离职', type: 'primary' },
])

/** 博客日志列表 */
const blogArr = ref<BlogItem[]>([])
/** 瀑布流加载中（Skeleton 骨架占位） */
const blogsLoading = ref(true)

// 挂载时：校验登录态并拉取用户资料、博客日志（与原 mounted 逻辑一致）
onMounted(() => {
  loginStatus()
  getAllLogs()
})

/**
 * 函数功能：获取全部博客日志（瀑布流数据源，带加载态反馈）
 * 输入参数：无
 * 输出参数：无（成功后写入 blogArr，无论结果如何结束 blogsLoading）
 * 返回值：无
 */
function getAllLogs(): void {
  blogsLoading.value = true
  request
    .post('/guest/get_all_logs')
    .then((res: any) => {
      if (res.code == 200) {
        blogArr.value = Array.isArray(res.data) ? res.data : []
      }
    })
    .finally(() => {
      blogsLoading.value = false
    })
}

/**
 * 函数功能：点击博客卡片跳转到博客详情页（显式传递详情页所需字段，
 *           避免整对象传参时 null/对象值被 Vue Router 丢弃）
 * 输入参数：item - 被点击的博客对象（取 id/title/upload_time/text/group_name 传递）
 * 输出参数：无
 * 返回值：无
 */
function showThisBlog(item: BlogItem): void {
  router.push({
    name: 'blog_show',
    params: {
      id: item.id ?? 0,
      title: item.title ?? '',
      upload_time: item.upload_time ?? '',
      text: item.text ?? '',
      group_name: item.group_name ?? '',
    },
  })
}

/**
 * 函数功能：登录状态校验——读取 localStorage "honey-user"，已登录则拉取用户最新资料
 *           （badge 标签 / timeline 时间线覆盖默认值，数据库字段为空时回退
 *            localStorage 本地存档，与个人设置页保存联动），未登录跳转 /login
 * 输入参数：无
 * 输出参数：无（可能更新 userTagArr / timelineArr，或触发路由跳转）
 * 返回值：无
 */
function loginStatus(): void {
  const loginStatusString = localStorage.getItem('honey-user')
  if (loginStatusString != null) {
    const loginStatus = JSON.parse(loginStatusString)
    if (loginStatus) {
      const userId = loginStatus.id
      // 获取用户最新数据
      request.get('/user/select_user_msg/' + userId).then((res: any) => {
        if (res.code == 200) {
          const data = res.data
          // 数据库字段为空时回退本地存档（设置页保存时双写，后端落库前的联动兜底）
          const badge = data.badge || localStorage.getItem(`honey-user-badge-${userId}`) || ''
          const timeline =
            data.timeline || localStorage.getItem(`honey-user-timeline-${userId}`) || ''
          // 解析标签（颜色按索引稳定轮换，避免每次刷新随机变化）
          if (badge) {
            try {
              const tags = JSON.parse(badge)
              const types = ['danger', 'primary', 'success', 'warning', 'info']
              userTagArr.value = tags.map((tag: string, index: number) => ({
                type: types[index % types.length],
                text: tag,
              }))
            } catch (e) {
              console.log('标签解析失败', e)
            }
          }
          // 解析时间线
          if (timeline) {
            try {
              timelineArr.value = JSON.parse(timeline)
            } catch (e) {
              console.log('时间线解析失败', e)
            }
          }
        }
      })
    }
  } else {
    router.push('/login')
  }
}
</script>

<style scoped>
/* ===== 顶部欢迎区 ===== */
.ac-home__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 8px 4px 0;
}

.ac-home__welcome {
  margin: 12px 0 0;
  color: #725d42;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

/* ===== 主体两栏布局：间距统一 + 侧边栏 sticky ===== */
.ac-home__body {
  margin-top: 16px;
  gap: 24px;
}

.ac-home__aside {
  padding: 0;
  flex-shrink: 0;
  /* 滚动长页面时个人信息卡片固定在可视区顶部 */
  position: sticky;
  top: 20px;
  align-self: flex-start;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
}

.ac-home__main {
  padding: 0;
  min-width: 0; /* 防止内容撑破 flex 布局产生横向滚动条 */
}

/* ===== 左侧个人信息卡片 ===== */
.user-card {
  margin-left: 0;
  min-height: 60vh;
}

.user-card__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-top: 6px;
}

.user-card__avatar {
  border: 4px solid #fff;
  box-shadow: 0 3px 10px 0 rgba(61, 52, 40, 0.15);
}

.user-card__name {
  font-size: 18px;
  font-weight: 800;
  color: #725d42;
}

.user-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.user-card__timeline {
  padding: 0;
  margin: 0;
}

/* ===== 右侧博客瀑布流 ===== */
.blog-card {
  min-height: 80vh;
}

.blog-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.masonry-container {
  column-count: 3;
  column-gap: 20px;
}

@media (max-width: 1200px) {
  .masonry-container {
    column-count: 2;
  }
}

@media (max-width: 768px) {
  .masonry-container {
    column-count: 1;
  }
}

/* 瀑布流加载骨架（动森 Skeleton 占位卡片） */
.masonry-skeleton {
  break-inside: avoid;
  margin-bottom: 20px;
}

.masonry-skeleton__text {
  margin-top: 10px;
}

.masonry-item {
  break-inside: avoid;
  margin-bottom: 20px;
  cursor: pointer;
  transition:
    transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.25s;
}

.masonry-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px 0 rgba(61, 52, 40, 0.18);
}

/* 瀑布流图片：按原始宽高比自适应高度（覆盖组件库 img 的 height:100%） */
.masonry-item__pic :deep(.animal-image__img) {
  height: auto;
}

/* 图片加载前的最小占位高度，避免瀑布流卡片完全塌陷 */
.masonry-item__pic :deep(.animal-image) {
  min-height: 120px;
}

.masonry-item__title {
  padding: 12px 4px 2px;
  text-align: center;
  font-weight: 800;
  color: #725d42;
  word-break: break-all;
}

.masonry-empty {
  text-align: center;
  color: #9f927d;
  margin-top: 24px;
}

/* ===== 响应式：平板竖屏及以下，侧边栏与内容上下堆叠 ===== */
@media (max-width: 992px) {
  .ac-home__body {
    flex-direction: column;
    gap: 16px;
  }

  .ac-home__aside {
    width: 100% !important; /* 覆盖 el-aside 内联宽度变量 */
    position: static;
    max-height: none;
    overflow-y: visible;
  }

  .user-card {
    min-height: 0; /* 堆叠模式下取消最小高度限制 */
  }

  /* 时间线改为左右两列，减少纵向占用 */
  .user-card__timeline :deep(.el-timeline) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0 24px;
  }
}

/* ===== 响应式：手机端瀑布流单列 + 标题紧凑 ===== */
@media (max-width: 480px) {
  .ac-home__welcome {
    font-size: 13px;
  }

  .user-card__timeline :deep(.el-timeline) {
    grid-template-columns: 1fr;
  }
}
</style>
